import { useState } from 'react';
import { AxiosResponse } from 'axios';
import {
  refreshToken,
  fetchCurrentUser,
  signIn,
  signUp,
  validateEmail,
  validateReset,
  sendForgotPassword,
  recoverPassword,
} from 'api';
import useLocalStorage from 'hooks/useLocalStorage';
import { useUser } from 'contexts/userContext';
import useAxios from 'hooks/useAxios';
import { IAxiosResponse, IError, IErrorData } from 'types';
import { checkIfError } from 'utils/helpers';
import {
  IForgotPasswordRequest,
  IRecoverPasswordRequest,
  ISignUpRequest,
  ISignInResponse,
  IUserResourceWithRelations,
} from '../../server/types';

const useSignIn = () => {
  const [request, { loading, error }] = useAxios<ISignInResponse>();
  const [token, setToken] = useLocalStorage('variant-token');
  const [user, setUser] = useUser();

  const handleSignIn = async (email: string, password: string) => {
    const response = await request(signIn({ email, password }));

    if (checkIfError(response)) return response;

    if (response?.token) {
      setToken(response.token);
    }

    if (response?.user) {
      setUser(response.user);
    }

    return response;
  };

  return [handleSignIn, { data: { token, ...user }, loading, error: error?.data?.message }] as const;
};

export const useSignUp = () => {
  const [request, { loading, error }] = useAxios<IUserResourceWithRelations>();
  const [user, setUser] = useState<IUserResourceWithRelations>();

  const handleSignUp = async (data: ISignUpRequest) => {
    const response = await request(signUp(data));

    if (checkIfError(response)) return response;

    setUser(response);

    return response;
  };

  return [handleSignUp, { data: user, loading, error: error?.data?.message }] as const;
};

const useValidateEmail = () => {
  const validateEmailAuth = async (email: string) => {
    let response;

    try {
      response = (await validateEmail({ email })) as string;
    } catch (err) {
      response = err as AxiosResponse<IError, IError>;
    }

    return response;
  };

  return [validateEmailAuth] as const;
};

const useValidateReset = () => {
  const [request, { loading, error }] = useAxios<IUserResourceWithRelations>();

  const [tokenError, setTokenError] = useState<string>();

  const handleValidateReset = async (token: string) => {
    if (!token) {
      setTokenError('Invalid link.');
    }
    return request(validateReset({ token }));
  };

  return [handleValidateReset, { loading, error: tokenError ?? error?.data?.message }] as const;
};

const useForgotPassword = () => {
  const [request, { loading, error }] = useAxios<string>();
  const [message, setMessage] = useState<string>();

  const handleSendForgotPassword = async ({ email, url }: IForgotPasswordRequest) => {
    const response = await request(sendForgotPassword({ email, url }));

    if (checkIfError(response)) {
      return response;
    }

    setMessage(response);

    return response;
  };

  return [handleSendForgotPassword, { loading, error: error?.data?.message, data: message }] as const;
};

const useRecoverPassword = () => {
  const [request, { loading, error }] = useAxios<void>();

  const handleRecoverPassword = async ({ password, token }: IRecoverPasswordRequest) =>
    request(recoverPassword({ password, token }));

  return [handleRecoverPassword, { loading, error: error?.data?.message }] as const;
};

const useAuthUser = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<IUserResourceWithRelations>();
  const [error, setError] = useState<string>();
  const [token, setToken] = useLocalStorage('variant-token');

  const refreshAuthToken = async () => {
    try {
      const response = await refreshToken();

      return response;
    } catch (e) {
      throw new Error((e as IErrorData).data?.message);
    }
  };

  const getAuthToken = async () => {
    const response = await refreshAuthToken();

    if (checkIfError(response)) {
      return response;
    }

    setToken(response?.token);

    return response?.token ?? false;
  };

  const getAuthUser = async () => {
    if (!token) {
      setLoading(false);
      return false;
    }

    try {
      const newToken = await getAuthToken();

      if (!newToken) return false;

      const userResponse = await fetchCurrentUser(newToken as string);

      if (checkIfError(userResponse)) {
        return false;
      }

      setUser(userResponse);

      setLoading(false);

      return userResponse;
    } catch (err) {
      setError((err as IAxiosResponse).message);
      setLoading(false);
      return false;
    }
  };

  return [getAuthUser, { loading, error, data: user }] as const;
};

export { useSignIn, useValidateEmail, useValidateReset, useAuthUser, useForgotPassword, useRecoverPassword };
